// @ts-nocheck
/*
 * ChatbotChannelsTab — where the AI Assistant answers.
 *
 * One assistant (one knowledge base, one tone, one set of stats) reached on
 * several channels. Each channel is a tile with its live status, so the owner
 * sees at a glance where the assistant is on and what still needs setting up;
 * the selected channel's full setup opens below the tiles, at full width,
 * because WhatsApp's recipients table and workflow editor need the room.
 *
 * A new channel (Slack, Teams) is one more entry in the tiles and one more
 * panel below. It used to be a Website / WhatsApp toggle, which grew a button
 * per channel and showed no status.
 */
import React, { useEffect, useState } from 'react';
import {
  Globe, MessageCircle, Copy, Check, ExternalLink, Palette,
  Smartphone, Rocket, Eye, QrCode, MessageSquareText, Plus,
} from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import WhatsAppDeploymentPanel from './whatsapp/WhatsAppDeploymentPanel';
import { whatsappApi } from '../../services/whatsappApi';
import ChatInterface from '../ChatInterface';

interface ChatbotChannelsTabProps {
  clientId: string;
  profile: any;
}

type Channel = 'website' | 'whatsapp';

// The WhatsApp channel's status in words a business owner reads at a glance.
const WHATSAPP_STATUS: Record<string, { label: string; tone: string }> = {
  none:    { label: 'Not set up',      tone: 'bg-gray-100 text-gray-600' },
  pending: { label: 'Setting up',      tone: 'bg-amber-50 text-amber-700' },
  active:  { label: 'Live',            tone: 'bg-emerald-50 text-emerald-700' },
  paused:  { label: 'Paused',          tone: 'bg-amber-50 text-amber-700' },
  error:   { label: 'Needs attention', tone: 'bg-red-50 text-red-700' },
};

const hostOf = (url: string) => {
  try { return new URL(url).host.replace(/^www\./, ''); } catch { return ''; }
};

const ChatbotChannelsTab: React.FC<ChatbotChannelsTabProps> = ({ clientId, profile }) => {
  const navigate = useNavigate();
  const location = useLocation();
  // ?channel=whatsapp opens on the WhatsApp channel: the marketplace's WhatsApp
  // card, Orbit and the old /whatsapp address all land here.
  const [activeChannel, setActiveChannel] = useState<Channel>(
    new URLSearchParams(location.search).get('channel') === 'whatsapp' ? 'whatsapp' : 'website'
  );
  // undefined while loading, null when WhatsApp has never been set up.
  const [waChannel, setWaChannel] = useState<any>(undefined);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    whatsappApi.listChannels(clientId)
      .then((list) => { if (!cancelled) setWaChannel(list.find((c) => c.channel_type === 'whatsapp') || null); })
      .catch(() => { if (!cancelled) setWaChannel(null); });
    return () => { cancelled = true; };
  }, [clientId]);

  const selectChannel = (channel: Channel) => {
    setActiveChannel(channel);
    navigate(
      { search: channel === 'whatsapp' ? '?channel=whatsapp' : '', hash: '#channels' },
      { replace: true },
    );
  };

  const currentDomain = window.location.origin;
  const widgetCode = `<!-- AI Chat Widget -->\n<script src="${currentDomain}/widget/widget-loader.js" data-client-id="${clientId}"></script>`;
  const websiteUrl = profile?.website_url || '';
  const previewPath = `/preview/${clientId}${websiteUrl ? `?url=${encodeURIComponent(websiteUrl)}` : ''}`;
  const previewUrl = previewPath;            // legacy alias (used by iframe + Full Preview button)
  const previewFullUrl = `${currentDomain}${previewPath}`;
  const businessName = profile?.name || 'Your business';
  const brandColor = profile?.branding?.primary_color || '#4F46E5';
  const [previewCopied, setPreviewCopied] = useState(false);
  const [previewView, setPreviewView] = useState<'try' | 'qr'>('try');
  const [assistantOpen, setAssistantOpen] = useState(false);

  // QR is generated by a public service so we don't need to install a JS lib.
  // The preview URL is already public-shareable, so passing it through QRserver
  // doesn't leak anything sensitive. Brand color tints the QR for a subtle
  // branded touch.
  const qrColor = (brandColor || '#4F46E5').replace('#', '');
  const qrApiUrl =
    `https://api.qrserver.com/v1/create-qr-code/` +
    `?size=240x240&margin=8&qzone=2` +
    `&data=${encodeURIComponent(previewFullUrl)}` +
    `&color=${qrColor}&bgcolor=FFFFFF`;

  const handleCopy = () => {
    navigator.clipboard.writeText(widgetCode);
    setCopied(true);
    toast.success('Script copied to clipboard!');
    setTimeout(() => setCopied(false), 2500);
  };

  const handleCopyPreview = () => {
    navigator.clipboard.writeText(previewFullUrl);
    setPreviewCopied(true);
    toast.success('Preview link copied!');
    setTimeout(() => setPreviewCopied(false), 2500);
  };

  const waStatus = WHATSAPP_STATUS[waChannel === null ? 'none' : waChannel?.status] || null;
  const waDetail = !waChannel
    ? 'Answer and message customers on WhatsApp Business'
    : [waChannel.display_name, waChannel.access_mode === 'public' ? 'Anyone can chat' : 'Invited recipients only']
        .filter(Boolean).join(' · ');
  const site = hostOf(websiteUrl);

  const tiles = [
    {
      id: 'website' as Channel,
      name: 'Website',
      icon: Globe,
      iconTone: 'bg-brand-100 text-brand-600',
      status: { label: 'Ready to embed', tone: 'bg-gray-100 text-gray-600' },
      detail: site ? `Chat widget for ${site}` : 'Chat widget for any website',
    },
    {
      id: 'whatsapp' as Channel,
      name: 'WhatsApp',
      icon: MessageCircle,
      iconTone: 'bg-emerald-50 text-emerald-600',
      status: waStatus,
      detail: waDetail,
    },
  ];

  return (
    <div className="space-y-6">
      {/* Channels: one tile per place the assistant answers, each with its status */}
      <div className="space-y-3">
        <p className="text-sm text-gray-500">
          Your assistant answers wherever you connect it. Knowledge, tone and stats are shared across every channel.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" role="group" aria-label="Channels">
          {tiles.map((tile) => {
            const Icon = tile.icon;
            const selected = activeChannel === tile.id;
            return (
              <button
                key={tile.id}
                type="button"
                aria-pressed={selected}
                onClick={() => selectChannel(tile.id)}
                className={`text-left rounded-xl border bg-white p-4 flex flex-col gap-3 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40 ${
                  selected ? 'border-gray-900 ring-1 ring-gray-900' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <span className="flex items-center justify-between gap-3">
                  <span className="flex items-center gap-2.5 min-w-0">
                    <span className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${tile.iconTone}`}>
                      <Icon size={18} />
                    </span>
                    <span className="text-sm font-semibold text-gray-900">{tile.name}</span>
                  </span>
                  {tile.status ? (
                    <span className={`shrink-0 whitespace-nowrap px-2 py-0.5 rounded-full text-[11px] font-medium ${tile.status.tone}`}>
                      {tile.status.label}
                    </span>
                  ) : (
                    <span className="shrink-0 h-4 w-14 rounded-full bg-gray-100 animate-pulse" aria-label="Loading status" />
                  )}
                </span>
                <span className="text-xs text-gray-500 leading-relaxed truncate">{tile.detail}</span>
              </button>
            );
          })}
          <div className="rounded-xl border border-dashed border-gray-200 p-4 flex flex-col gap-3 text-gray-400">
            <span className="flex items-center gap-2.5">
              <span className="w-9 h-9 rounded-lg flex items-center justify-center bg-gray-50">
                <Plus size={18} />
              </span>
              <span className="text-sm font-semibold text-gray-500">More channels</span>
            </span>
            <span className="text-xs leading-relaxed">Slack and Microsoft Teams are next. Each appears here when it's ready.</span>
          </div>
        </div>
      </div>

      {/* Website Integration */}
      {activeChannel === 'website' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left: Embed script + customize shortcut */}
          <div className="space-y-5">
            {/* Embed script — primary action now that the preview hero is gone */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200">
                <div>
                  <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Embed on your website</span>
                  <p className="text-[11px] text-gray-400 mt-0.5">When ready, paste this script before <code>&lt;/body&gt;</code></p>
                </div>
                <button
                  onClick={handleCopy}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                    copied
                      ? 'bg-emerald-50 text-emerald-600'
                      : 'bg-brand-50 text-brand-600 hover:bg-brand-100'
                  }`}
                >
                  {copied ? <Check size={14} /> : <Copy size={14} />}
                  {copied ? 'Copied!' : 'Copy script'}
                </button>
              </div>
              <pre className="p-4 text-sm text-gray-800 bg-gray-900/[0.02] overflow-x-auto font-sans leading-relaxed">
                <code>{widgetCode}</code>
              </pre>
            </div>

            {/* Customize shortcut */}
            <div>
              <button
                onClick={() => navigate('/marketplace/chatbot#customize')}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <Palette size={16} />
                Customize appearance & behavior
              </button>
            </div>
          </div>

          {/* Right: simple test panel — one button to open the AI Assistant
              in a full tab (where the widget reliably loads), plus a QR for
              mobile testing. No more inline-widget / iframe shenanigans. */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col">
            <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
              <div className="inline-flex bg-white border border-gray-200 rounded-lg p-0.5 text-xs">
                <button
                  onClick={() => setPreviewView('try')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md font-medium transition-colors ${
                    previewView === 'try' ? 'bg-brand-50 text-brand-700' : 'text-gray-500 hover:text-gray-800'
                  }`}
                >
                  <MessageSquareText size={13} />
                  Test on this device
                </button>
                <button
                  onClick={() => setPreviewView('qr')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md font-medium transition-colors ${
                    previewView === 'qr' ? 'bg-brand-50 text-brand-700' : 'text-gray-500 hover:text-gray-800'
                  }`}
                >
                  <QrCode size={13} />
                  Scan on phone
                </button>
              </div>
            </div>

            {/* QR view */}
            {previewView === 'qr' && (
              <div className="flex-1 flex flex-col items-center justify-center p-8 bg-gradient-to-b from-white to-brand-50/30 min-h-[480px]">
                <div
                  className="rounded-2xl bg-white p-5 shadow-sm border-2"
                  style={{ borderColor: brandColor }}
                >
                  <img
                    src={qrApiUrl}
                    alt="Scan to test the chatbot on your phone"
                    width={240}
                    height={240}
                    className="block"
                    loading="lazy"
                  />
                </div>

                {/* Brand badge below QR */}
                <div className="mt-5 flex items-center gap-2">
                  <Smartphone size={14} className="text-gray-400" />
                  <span className="text-sm font-medium text-gray-900">
                    Scan with your phone to test
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1.5 text-center max-w-[280px] leading-relaxed">
                  Opens the chatbot in your phone's browser. No login needed — just chat to verify it answers correctly.
                </p>

                {/* Mini share row */}
                <div className="mt-5 flex items-center gap-2">
                  <button
                    onClick={handleCopyPreview}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                      previewCopied
                        ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                        : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {previewCopied ? <Check size={12} /> : <Copy size={12} />}
                    {previewCopied ? 'Copied' : 'Copy link'}
                  </button>
                  <a
                    href={`mailto:?subject=${encodeURIComponent(`Test the ${businessName} chatbot`)}&body=${encodeURIComponent(
                      `Hey — here's a preview link to test our new AI chatbot:\n\n${previewFullUrl}\n\nIt's trained on ${businessName}'s data. Open it on any device and chat — let me know what you think.`,
                    )}`}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-white border border-gray-200 text-gray-700 hover:bg-gray-50"
                    title="Email this preview link"
                  >
                    <ExternalLink size={12} />
                    Email link
                  </a>
                </div>
              </div>
            )}

            {/* Test on this device — single CTA opens the AI Assistant. */}
            {previewView === 'try' && (
              <div className="flex-1 flex flex-col items-center justify-center p-8 bg-gradient-to-b from-white to-brand-50/20 min-h-[480px] text-center">
                <div className="w-14 h-14 rounded-2xl bg-brand-100 flex items-center justify-center mb-4">
                  <MessageSquareText size={26} className="text-brand-600" />
                </div>
                <h3 className="text-base font-semibold text-gray-900 mb-1">
                  Open the AI Assistant
                </h3>
                <p className="text-xs text-gray-500 max-w-[280px] leading-relaxed mb-5">
                  The same chatbot your customers see on their site, trained on{' '}
                  <strong>{businessName}</strong>'s data.
                </p>
                <button
                  onClick={() => setAssistantOpen(true)}
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-medium bg-brand-600 text-white hover:bg-brand-700 transition-colors shadow-sm whitespace-nowrap"
                >
                  <MessageSquareText size={16} />
                  Chat with AI Assistant
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* WhatsApp Integration */}
      {activeChannel === 'whatsapp' && (
        <div className="space-y-6">
          <WhatsAppDeploymentPanel
            clientId={clientId}
            userRole={(localStorage.getItem('user_role') || '').toLowerCase()}
            businessName={profile?.name}
            botName={profile?.bot_name}
            onChannelLoaded={setWaChannel}
          />
        </div>
      )}

      {assistantOpen && (
        <ChatInterface
          clientId={clientId}
          isVisible={true}
          onClose={() => setAssistantOpen(false)}
        />
      )}
    </div>
  );
};

export default ChatbotChannelsTab;
